import CryptoJS from 'crypto-js';

export class CryptoService {
  private static masterKey: string | null = null;

  static generateMasterKey(): string {
    const randomBytes = CryptoJS.lib.WordArray.random(32);
    return randomBytes.toString(CryptoJS.enc.Base64);
  }

  static deriveSessionKey(email: string, password: string): string {
    return CryptoJS.SHA256(email + password).toString();
  }

  static encryptMasterKey(masterKey: string, userPassword: string, userEmail: string): string {
    const sessionKey = this.deriveSessionKey(userEmail, userPassword);
    return CryptoJS.AES.encrypt(masterKey, sessionKey).toString();
  }

  static decryptMasterKey(encryptedMasterKey: string, userPassword: string, userEmail: string): string | null {
    try {
      const sessionKey = this.deriveSessionKey(userEmail, userPassword);
      const bytes = CryptoJS.AES.decrypt(encryptedMasterKey, sessionKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      if (!decrypted) {
        return null;
      }
      return decrypted;
    } catch (error) {
      return null;
    }
  }

  static setMasterKey(masterKey: string, _email?: string, _password?: string) {
    this.masterKey = masterKey;
  }

  static clearEncryptionKey() {
    this.masterKey = null;
  }

  static getMasterKey(): string | null {
    return this.masterKey;
  }

  static encrypt(text: string): string {
    if (!this.masterKey) {
      throw new Error('Master key not set');
    }
    return CryptoJS.AES.encrypt(text, this.masterKey).toString();
  }

  static decrypt(encryptedText: string): string {
    if (!this.masterKey) {
      throw new Error('Master key not set');
    }
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedText, this.masterKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      if (!decrypted) {
        throw new Error('Failed to decrypt');
      }
      return decrypted;
    } catch (error) {
      return '[Unable to decrypt]';
    }
  }

  static async encryptFile(file: Blob): Promise<Blob> {
    if (!this.masterKey) {
      throw new Error('Master key not set');
    }

    const arrayBuffer = await file.arrayBuffer();
    const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer as any);
    const encrypted = CryptoJS.AES.encrypt(wordArray, this.masterKey).toString();

    return new Blob([encrypted], { type: 'application/octet-stream' });
  }

  static async decryptFile(encryptedBlob: Blob): Promise<Blob> {
    if (!this.masterKey) {
      console.error('Master key not set');
      throw new Error('Master key not set');
    }

    console.log('Decrypting file, blob size:', encryptedBlob.size);
    const encryptedText = await encryptedBlob.text();
    console.log('Encrypted text length:', encryptedText.length);

    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedText, this.masterKey);
      console.log('Decrypted sigBytes:', decrypted.sigBytes);

      if (decrypted.sigBytes <= 0) {
        throw new Error('Decryption resulted in empty data');
      }

      const typedArray = this.wordArrayToUint8Array(decrypted);
      console.log('Converted to typed array, length:', typedArray.length);

      const mimeType = this.detectMimeType(typedArray);
      console.log('Detected MIME type:', mimeType);

      const blob = new Blob([new Uint8Array(typedArray)], { type: mimeType });
      console.log('Created blob with type:', blob.type, 'size:', blob.size);

      return blob;
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt file');
    }
  }

  private static wordArrayToUint8Array(wordArray: CryptoJS.lib.WordArray): Uint8Array {
    const words = wordArray.words;
    const sigBytes = wordArray.sigBytes;
    const u8 = new Uint8Array(sigBytes);

    for (let i = 0; i < sigBytes; i++) {
      u8[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    }

    return u8;
  }

  private static detectMimeType(bytes: Uint8Array): string {
    if (bytes.length < 4) {
      return 'application/octet-stream';
    }

    const signature = Array.from(bytes.slice(0, 12))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');

    if (signature.startsWith('ffd8ff')) {
      return 'image/jpeg';
    }
    if (signature.startsWith('89504e47')) {
      return 'image/png';
    }
    if (signature.startsWith('47494638')) {
      return 'image/gif';
    }
    if (signature.startsWith('52494646') && signature.substring(16, 24) === '57454250') {
      return 'image/webp';
    }
    if (signature.startsWith('000000') && (signature.substring(8, 16) === '66747970' || signature.substring(8, 16) === '6d646174')) {
      return 'video/mp4';
    }
    if (signature.startsWith('1a45dfa3')) {
      return 'video/webm';
    }
    if (signature.startsWith('494433') || signature.startsWith('fffb') || signature.startsWith('fff3')) {
      return 'audio/mpeg';
    }

    return 'application/octet-stream';
  }

  static hasEncryptionKey(): boolean {
    return this.masterKey !== null;
  }

  private static async deriveKeyFromMasterKey(): Promise<CryptoKey> {
    if (!this.masterKey) {
      throw new Error('Master key not set');
    }

    const encoder = new TextEncoder();
    const keyMaterial = encoder.encode(this.masterKey);

    const hash = await crypto.subtle.digest('SHA-256', keyMaterial);

    return crypto.subtle.importKey(
      'raw',
      hash,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  static async encryptChunk(chunk: ArrayBuffer): Promise<{ encrypted: ArrayBuffer; iv: string }> {
    const key = await this.deriveKeyFromMasterKey();

    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      chunk
    );

    const ivBase64 = btoa(String.fromCharCode(...Array.from(iv)));

    return { encrypted, iv: ivBase64 };
  }

  static async decryptChunk(encryptedChunk: ArrayBuffer, ivBase64: string): Promise<ArrayBuffer> {
    const key = await this.deriveKeyFromMasterKey();

    const ivString = atob(ivBase64);
    const iv = new Uint8Array(ivString.length);
    for (let i = 0; i < ivString.length; i++) {
      iv[i] = ivString.charCodeAt(i);
    }

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encryptedChunk
    );

    return decrypted;
  }

  static async calculateSHA256(data: ArrayBuffer): Promise<string> {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }
}
